import { Calendar } from 'components/Calendar/Calendar'
import { usePostPreviewStore } from 'components/PostPreviewModalV2/PostPreviewStoreProvider'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'

import Styles from './PostScheduler.module.scss'
import { Button } from 'blaze-ui/Button'
import { Calendar2 } from 'blaze-ui/icons'
import { PostPreviewScreens } from 'components/PostPreviewModalV2/PostPreviewStore'
import { TimeValue } from '@react-aria/datepicker'
import { Time } from '@internationalized/date'

import TimePicker from 'blaze-ui/TimePicker/TimePicker'
import { usePostScheduleService } from 'components/PostPreviewModalV2/hooks/usePostScheduleService'
import { usePostPreviewContext } from 'components/PostPreviewModal/PostPreviewContext'
import { PostAction } from 'components/PostPreviewModalV2/hooks/usePostActionService'
import { pluralize } from 'utils'
import { Section } from 'components/PostPreviewModalV2/components/Section/Section'
import { YoutubePostDetails } from 'components/PostPreviewModal/components/Youtube/YoutubePostDetails'
import { DesignChannel } from 'components/NewFileModal/Types'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'

export const PostScheduler = observer(({ action }: { action: PostAction.AutoSchedule | PostAction.Reminder }) => {
  const store = usePostPreviewStore()
  const { schedule } = usePostScheduleService({ action, onComplete: store.onFilesCreated })
  const { scheduledPost } = usePostPreviewContext()

  const currentTime = dayjs(store.postAt)

  /* ---------------------------------- date / time --------------------------------- */
  const handleTimeChange = (t: TimeValue | null) => {
    if (!t) return
    const updated = dayjs(store.postAt).hour(t.hour).minute(t.minute)
    store.setPostAt(updated.toISOString())
  }
  const handleDateChange = (d?: string) => {
    if (!d) return
    const updated = dayjs(d).hour(currentTime.hour()).minute(currentTime.minute())
    store.setPostAt(updated.toISOString())
  }

  /* ---------------------------------- CTA --------------------------------- */
  const handleSchedule = async () => {
    if (!scheduledPost) return
    store.setScreen(PostPreviewScreens.RESULT_SCREEN)
    await schedule(scheduledPost.content || '', scheduledPost.source_type, scheduledPost.source_id, store.postAt)
  }

  const isUpdatingOriginal = store.flow === 'edit' // ✨ new check
  const verb = isUpdatingOriginal ? 'Update' : 'Schedule'

  const isReminder = action === PostAction.Reminder
  const title = isReminder
    ? 'Select date & time to send reminder'
    : `Select date & time to publish${store.flow === 'edit' ? ' this post' : ' all posts'}`
  const description = isReminder
    ? 'Reminder will be sent at the selected time'
    : 'All posts will be scheduled at the same time'
  const buttonText = isReminder
    ? `${verb} ${pluralize('reminder', store.selectedAccounts.length)}`
    : `${verb} ${pluralize('post', store.selectedAccounts.length)}`

  return (
    <Section title={title} description={description}>
      <div className={Styles.container}>
        <div className={Styles.calendarContainer}>
          <Calendar
            responsive
            isDateUnavailable={(d: string) => dayjs(d).isBefore(dayjs(), 'day')}
            value={dayjs(store.postAt).format('YYYY-MM-DD')}
            onChange={handleDateChange}
          />
          <div className={Styles.timePickerContainer}>
            <span className={Styles.timePickerLabel}>Time of day in your time zone</span>
            <TimePicker value={new Time(currentTime.hour(), currentTime.minute())} onChange={handleTimeChange} />
          </div>
        </div>

        {store.channel === DesignChannel.YoutubeShort && (
          <YoutubePostDetails scheduledPost={scheduledPost as ScheduledPostShow} channel={store.channel} />
        )}

        <div className={Styles.buttonContainer}>
          <span className={Styles.dateText}>{dayjs(store.postAt).format('ddd, MMM D [at] h:mma z')}</span>

          <Button variant="primary" size="lg" frontIcon={Calendar2} fullWidth onPress={handleSchedule}>
            {buttonText}
          </Button>

          <Button
            variant="tertiary"
            size="lg"
            fullWidth
            onPress={() =>
              store.setScreen(isUpdatingOriginal ? PostPreviewScreens.RESULT_SCREEN : PostPreviewScreens.CHANNEL_SCREEN)
            }
          >
            Cancel
          </Button>
        </div>
      </div>
    </Section>
  )
})
